'use client'

import PropTypes from 'prop-types'
import { EditIcon } from '@repo/ui/EditIcon'
import { ExportIcon } from '@repo/ui/ExportIcon'
import { GoLiveIcon } from '@repo/ui/GoLiveIcon'
import { NavigationMenuItem } from '@repo/ui/NavigationMenuItem'
import { OverflowIcon } from '@repo/ui/OverflowIcon'
import { PopupWithBackdrop } from '@repo/ui/PopupWithBackdrop'
import { RestoreIcon } from '@repo/ui/RestoreIcon'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useMenuState, MenuItem, SubMenu, MenuDivider } from '@szhsin/react-menu'
import { useEffect, useState, useRef } from 'react'
import { useUndertakingsStore } from '@repo/store/useUndertakingsStore'
import { useUrlStore } from '@repo/store/useUrlStore'
import MUIIconButton from '@mui/material/IconButton'

export const MatterWorkspaceActionsMenu = ({ workspace = 'matter' }) => {
  const ref = useRef(null)
  const [menuState, toggleMenu] = useMenuState({ transition: true })
  const { currentMatter } = useMattersStore()
  const { fetchMatterUndertakingCount, isUndertakingsSupported, undertakingsCounts } = useUndertakingsStore()
  const { setPostLoadAction, navigateWithClientMatterNo } = useUrlStore()

  const [matterHasOutstandingUndertakings, setMatterHasOutstandingUndertakings] = useState(false)

  const triggerPostLoadAction = ({ postLoadAction, postLoadSelector }) => {
    setPostLoadAction({
      postLoadAction,
      postLoadActionShouldWaitForIframeToLoad: false,
      postLoadSelector,
      postLoadPage: 'matterpage.aspx'
    })
  }

  const editMatter = () => {
    if (!currentMatter) return
    navigateWithClientMatterNo({
      route: `/case-management/matters/matter-detail/edit-matter`,
      clientMatterNo: currentMatter.clientMatterNo,
      appendClientMatterNo: true
    })
  }

  const archiveMatter = () => {
    if (!currentMatter) return
    navigateWithClientMatterNo({
      route: `/case-management/matters/matter-archive`,
      clientMatterNo: currentMatter.clientMatterNo,
      appendClientMatterNo: true
    })
  }

  const restoreMatter = () => {
    toggleMenu(false)
    triggerPostLoadAction({
      postLoadAction: 'restoreMatter',
      postLoadSelector: '#ctl00_MainContentPlaceHolder_ArchRestBtn'
    })
  }

  const editClient = () => {
    if (!currentMatter) return
    navigateWithClientMatterNo({
      route: `/case-management/clients/client-detail/edit-client`,
      clientMatterNo: currentMatter.clientMatterNo,
      appendClientMatterNo: true
    })
  }

  // OS-2715/OS-2716/OS-2717 (defects 56/57/58): laaCertificateGrid.aspx/tbillallopoa.aspx
  // have no menu entry anywhere in the product, and matterPage.aspx's own Legal Aid fields
  // (Certificate:/Escape Status:/CostsMaster export) are never shown from React at all --
  // three bridges to the existing, already-working legacy screens, reusing this same
  // navigateWithClientMatterNo pattern editMatter/archiveMatter/editClient already use.
  // Shown unconditionally (not gated on Private/Legal Aid) -- currentMatter carries no such
  // flag in this store today, and each target legacy screen already handles a non-Legal-Aid
  // matter gracefully on its own (an empty/no-op view, not an error).
  const manageCertificates = () => {
    if (!currentMatter) return
    navigateWithClientMatterNo({
      route: `/case-management/matters/matter-workspace/certificates`,
      clientMatterNo: currentMatter.clientMatterNo,
      appendClientMatterNo: true
    })
  }

  const poaInterimClaim = () => {
    if (!currentMatter) return
    navigateWithClientMatterNo({
      route: `/case-management/matters/matter-workspace/poa-interim-claim`,
      clientMatterNo: currentMatter.clientMatterNo,
      appendClientMatterNo: true
    })
  }

  const legalAidDetails = () => {
    if (!currentMatter) return
    navigateWithClientMatterNo({
      route: `/case-management/matters/matter-workspace/legal-aid-classic`,
      clientMatterNo: currentMatter.clientMatterNo,
      appendClientMatterNo: true
    })
  }

  const goLive = ({ writeOffTime }) => {
    toggleMenu(false)

    // if we want to write off time, we need to check the write off time checkbox
    const iframe = document.querySelector('iframe[name="ospreyIframe"]')
    const iframeDocument = iframe?.contentWindow?.document
    const element = iframeDocument?.querySelector(`#ctl00_MainContentPlaceHolder_chkWriteOffTime`)
    if (element) element.checked = writeOffTime

    triggerPostLoadAction({
      postLoadAction: 'goLive',
      postLoadSelector: '#ctl00_MainContentPlaceHolder_btnGoLive'
    })
  }

  useEffect(() => {
    if (!isUndertakingsSupported || !currentMatter) return

    fetchMatterUndertakingCount({
      clientNo: currentMatter.clientNo,
      matterNo: currentMatter.matterNo
    })
  }, [currentMatter, fetchMatterUndertakingCount, isUndertakingsSupported])

  useEffect(() => {
    if (!currentMatter || !Array.isArray(undertakingsCounts)) {
      setMatterHasOutstandingUndertakings(false)
      return
    }

    const match = undertakingsCounts.find(
      (item) => item.clientNo === currentMatter.clientNo && item.matterNo === currentMatter.matterNo
    )

    if (!match) {
      setMatterHasOutstandingUndertakings(false)
      return
    }

    setMatterHasOutstandingUndertakings(Boolean(match.matterHasOutstandingUndertakings))
  }, [undertakingsCounts, currentMatter])

  return (
    <>
      <MUIIconButton
        data-testid="MatterWorkspaceActionsMenu"
        onClick={() => toggleMenu(true)}
        ref={ref}
        sx={{ margin: 0, marginTop: -1, width: '36px', height: '36px', right: '6px' }}
      >
        <OverflowIcon />
      </MUIIconButton>

      <PopupWithBackdrop
        menuState={menuState}
        anchorRef={ref}
        toggleMenu={toggleMenu}
        align="end"
        className="matterWorkspaceActionMenu"
        arrow
      >
        {workspace === 'matter' ? (
          <>
            <MenuItem onClick={editMatter}>
              <EditIcon sx={{ height: '20px', width: '22px' }} />
              Edit matter
            </MenuItem>

            {currentMatter?.dateArchived ? (
              <MenuItem onClick={restoreMatter} disabled={isUndertakingsSupported && matterHasOutstandingUndertakings}>
                <RestoreIcon sx={{ height: '20px', width: '22px' }} />
                Restore matter
              </MenuItem>
            ) : (
              <MenuItem onClick={archiveMatter} disabled={isUndertakingsSupported && matterHasOutstandingUndertakings}>
                <ExportIcon sx={{ height: '20px', width: '22px' }} />
                Archive matter
              </MenuItem>
            )}

            {currentMatter?.prospect ? (
              <SubMenu
                label={NavigationMenuItem({
                  label: 'Go live',
                  icon: <GoLiveIcon sx={{ height: '20px', width: '22px' }} />,
                  isSubMenu: true,
                  sx: { cursor: 'default' }
                })}
              >
                <MenuItem onClick={() => goLive({ writeOffTime: false })}>Go live and keep time</MenuItem>
                <MenuItem onClick={() => goLive({ writeOffTime: true })}>Go live and undo time</MenuItem>
              </SubMenu>
            ) : null}

            <MenuDivider />

            {/* OS-2715/OS-2716/OS-2717 (defects 56/57/58) */}
            <MenuItem onClick={manageCertificates}>Manage Certificates</MenuItem>
            <MenuItem onClick={poaInterimClaim}>POA / Interim Claim</MenuItem>
            <MenuItem onClick={legalAidDetails}>Legal Aid details (classic view)</MenuItem>

            <MenuDivider />
          </>
        ) : null}

        <MenuItem onClick={editClient}>
          <EditIcon sx={{ height: '20px', width: '22px' }} />
          Edit client
        </MenuItem>
      </PopupWithBackdrop>
    </>
  )
}

MatterWorkspaceActionsMenu.propTypes = {
  workspace: PropTypes.string
}
